import type { Profile } from "../../profiles-tracking/components/edit-profile-modal/types";

type SelectedProfile = {
  profileBusinessId: string;
  profileStatus?: string;
};

type EditableProfile = {
  name?: string;
  city?: string;
  email?: string;
  status?: string;
};

type BuildParams = {
  selectedProfile: SelectedProfile;
  originalProfile: EditableProfile | null;
  updatedProfile: EditableProfile;
  token: string;
};

export const buildFollowingProfileUpdateParams = ({
  selectedProfile,
  originalProfile,
  updatedProfile,
  token,
}: BuildParams) => ({
  profileBusinessId: selectedProfile.profileBusinessId,
  ...(updatedProfile.name ? { razaoSocial: updatedProfile.name } : {}),
  ...(updatedProfile.city ? { locatidade: updatedProfile.city } : {}),
  ...(updatedProfile.email ? { email: updatedProfile.email } : {}),
  ...(updatedProfile.status &&
  updatedProfile.status !== originalProfile?.status
    ? { profileStatus: updatedProfile.status }
    : {}),
  token,
});

const isEmptyObject = (value: unknown) =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.keys(value).length === 0;

const comparable = (value: unknown) => {
  if (Array.isArray(value) && value.length === 0) return JSON.stringify(null);
  if (isEmptyObject(value)) return JSON.stringify(null);

  return JSON.stringify(value ?? null);
};

const hasChanged = (before: unknown, after: unknown) =>
  comparable(before) !== comparable(after);

const editableFields = [
  "name",
  "category",
  "categoryId",
  "shortName",
  "openingDate",
  "phone",
  "email",
  "website",
  "description",
  "hours",
  "secondaryCategories",
  "secondaryCategoryIds",
  "whatsapp",
  "socialMedia",
  "serviceAreas",
  "specialHours",
  "amenities",
  "accessibility",
  "parking",
  "payments",
  "serviceOptions",
] as const;

export const buildFollowingProfileUpdateData = (
  originalProfile: Profile | null,
  updatedProfile: Profile
): Partial<Profile> => {
  if (!originalProfile) return {};

  const updateData: Partial<Profile> = {};

  editableFields.forEach((field) => {
    if (hasChanged(originalProfile?.[field], updatedProfile[field])) {
      updateData[field] = updatedProfile[field] as never;
    }
  });

  if (hasChanged(originalProfile?.status, updatedProfile.status)) {
    updateData.status = updatedProfile.status;
  }

  return updateData;
};
